import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Columns,
  Eye,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Download,
  Sparkles,
  User,
  Move,
  ScanEye,
  Maximize,
} from 'lucide-react';

interface ComparisonSliderProps {
  originalUrl: string;
  restoredUrl: string;
  onDownload: () => void;
  versionLabel?: string;
  /** Optional export controls rendered next to Download (e.g. Google Photos save). */
  exportActions?: React.ReactNode;
  /** Left/before side label (default: Original). */
  beforeLabel?: string;
  /** Right/after side label (default: Restored). */
  afterLabel?: string;
  downloadLabel?: string;
}

export const ComparisonSlider: React.FC<ComparisonSliderProps> = ({
  originalUrl,
  restoredUrl,
  onDownload,
  versionLabel = 'Latest Restoration',
  exportActions,
  beforeLabel = 'Original',
  afterLabel = 'Restored',
  downloadLabel = 'Download',
}) => {
  const [sliderPosition, setSliderPosition] = useState<number>(50);
  const [isDraggingSlider, setIsDraggingSlider] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<'split' | 'before' | 'after' | 'face-zoom'>('split');
  const [zoom, setZoom] = useState<number>(1);

  // Pan offsets for zooming in on faces
  const [panOffset, setPanOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState<boolean>(false);
  const [dragStart, setDragStart] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  const containerRef = useRef<HTMLDivElement | null>(null);

  const handleSliderPointerDown = (e: React.PointerEvent) => {
    e.stopPropagation();
    setIsDraggingSlider(true);
    updateSlider(e.clientX);
  };

  const updateSlider = useCallback((clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const percentage = Math.round((x / rect.width) * 100);
    setSliderPosition(percentage);
  }, []);

  // Quick Face Zoom Preset: jumps directly to 300% zoom with upper-third vertical framing (where portrait faces sit)
  const handleToggleFaceZoomMode = () => {
    if (viewMode === 'face-zoom' || zoom >= 2.5) {
      // Toggle off
      setViewMode('split');
      setZoom(1);
      setPanOffset({ x: 0, y: 0 });
    } else {
      // Toggle on Face Zoom (3x magnification centered on face region)
      setViewMode('face-zoom');
      setZoom(3);
      setPanOffset({ x: 0, y: 80 }); // Pan down to reveal face in upper-third
    }
  };

  // Canvas Pan Handlers when zoomed
  const handleCanvasMouseDown = (e: React.MouseEvent) => {
    if (zoom > 1 && !isDraggingSlider) {
      setIsPanning(true);
      setDragStart({ x: e.clientX - panOffset.x, y: e.clientY - panOffset.y });
    }
  };

  useEffect(() => {
    const handlePointerMove = (e: PointerEvent) => {
      if (isDraggingSlider) {
        updateSlider(e.clientX);
      } else if (isPanning && zoom > 1) {
        const newX = e.clientX - dragStart.x;
        const newY = e.clientY - dragStart.y;
        setPanOffset({ x: newX, y: newY });
      }
    };

    const handlePointerUp = () => {
      setIsDraggingSlider(false);
      setIsPanning(false);
    };

    if (isDraggingSlider || isPanning) {
      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', handlePointerUp);
    }
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [isDraggingSlider, isPanning, dragStart, zoom, updateSlider]);

  const resetZoomAndPan = () => {
    setZoom(1);
    setPanOffset({ x: 0, y: 0 });
    if (viewMode === 'face-zoom') setViewMode('split');
  };

  return (
    <div className="flex flex-col w-full bg-stone-900 border border-stone-800 rounded-xl overflow-hidden shadow-2xl">
      {/* Control bar */}
      <div className="flex flex-col gap-2 px-3 sm:px-4 py-2.5 bg-stone-950/80 border-b border-stone-800">
        <div className="flex items-center justify-between gap-2 min-w-0">
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-teal-950 text-teal-300 border border-teal-800/60 truncate max-w-full">
            <Sparkles className="w-3 h-3 mr-1 shrink-0" />
            <span className="truncate">{versionLabel}</span>
          </span>
          <span className="text-xs text-stone-400 hidden lg:inline shrink-0">
            {zoom > 1
              ? 'Drag to pan around face & fine details'
              : 'Drag vertical divider to inspect corrections'}
          </span>
        </div>

        <div className="flex items-center flex-wrap gap-1.5">
          <button
            type="button"
            onClick={handleToggleFaceZoomMode}
            aria-label="Face zoom 300 percent"
            className={`min-h-10 px-2.5 py-1.5 rounded-lg text-xs font-semibold flex items-center space-x-1.5 transition-colors border shadow-sm ${
              viewMode === 'face-zoom' || zoom >= 2.5
                ? 'bg-teal-600 text-stone-950 border-teal-500 font-bold'
                : 'bg-stone-800 hover:bg-stone-700 text-teal-300 border-stone-700 hover:text-teal-200'
            }`}
            title="Magnify 300% to inspect facial bone structure, jaw, and likeness"
          >
            <ScanEye className="w-3.5 h-3.5" />
            <span className="hidden xs:inline sm:inline">Face Zoom</span>
          </button>

          <div className="inline-flex bg-stone-900 rounded-lg p-0.5 border border-stone-800 text-xs">
            <button
              type="button"
              onClick={() => {
                setViewMode('split');
                if (zoom === 1) setPanOffset({ x: 0, y: 0 });
              }}
              className={`min-h-10 px-2.5 py-1.5 rounded flex items-center space-x-1 transition-colors ${
                viewMode === 'split' ? 'bg-teal-600 text-white font-medium' : 'text-stone-400 hover:text-stone-200'
              }`}
              title="Split interactive slider"
              aria-label="Split view"
            >
              <Columns className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Split</span>
            </button>
            <button
              type="button"
              onClick={() => setViewMode('before')}
              className={`min-h-10 px-2.5 py-1.5 rounded flex items-center space-x-1 transition-colors ${
                viewMode === 'before' ? 'bg-teal-600 text-white font-medium' : 'text-stone-400 hover:text-stone-200'
              }`}
              title={`View ${beforeLabel}`}
              aria-label={`${beforeLabel} view`}
            >
              <Eye className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{beforeLabel}</span>
            </button>
            <button
              type="button"
              onClick={() => setViewMode('after')}
              className={`min-h-10 px-2.5 py-1.5 rounded flex items-center space-x-1 transition-colors ${
                viewMode === 'after' ? 'bg-teal-600 text-white font-medium' : 'text-stone-400 hover:text-stone-200'
              }`}
              title={`View ${afterLabel}`}
              aria-label={`${afterLabel} view`}
            >
              <Sparkles className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{afterLabel}</span>
            </button>
          </div>

          <div className="inline-flex items-center bg-stone-900 border border-stone-800 rounded-lg p-0.5 text-stone-400 text-xs min-h-10">
            {[1, 2, 3].map((z) => (
              <button
                key={z}
                type="button"
                onClick={() => {
                  setZoom(z);
                  if (z === 1) setPanOffset({ x: 0, y: 0 });
                }}
                className={`px-2 py-1.5 min-h-9 rounded transition-colors ${
                  Math.round(zoom) === z
                    ? 'bg-teal-600 text-white font-medium'
                    : 'hover:text-stone-100'
                }`}
                title={`Zoom ${z * 100}%`}
                aria-label={`Zoom ${z * 100}%`}
              >
                {z * 100}%
              </button>
            ))}
            <button
              type="button"
              onClick={() => setZoom((prev) => Math.max(1, +(prev - 0.5).toFixed(1)))}
              disabled={zoom <= 1}
              className="p-2 hover:text-stone-100 disabled:opacity-40 transition-colors"
              title="Zoom Out"
              aria-label="Zoom out"
            >
              <ZoomOut className="w-3.5 h-3.5" />
            </button>
            <span className="px-1.5 font-mono text-[11px] text-stone-300 min-w-[4ch] text-center">
              {Math.round(zoom * 100)}%
            </span>
            <button
              type="button"
              onClick={() => setZoom((prev) => Math.min(4.5, +(prev + 0.5).toFixed(1)))}
              disabled={zoom >= 4.5}
              className="p-2 hover:text-stone-100 disabled:opacity-40 transition-colors"
              title="Zoom In (Inspect Features)"
              aria-label="Zoom in"
            >
              <ZoomIn className="w-3.5 h-3.5" />
            </button>
            {(zoom > 1 || panOffset.x !== 0 || panOffset.y !== 0) && (
              <button
                type="button"
                onClick={resetZoomAndPan}
                className="p-2 hover:text-teal-400 transition-colors ml-0.5"
                title="Reset Zoom & Pan to 100%"
                aria-label="Reset zoom"
              >
                <RotateCcw className="w-3 h-3" />
              </button>
            )}
          </div>

          <div className="flex items-center flex-wrap gap-1.5 ml-auto">
            <button
              type="button"
              onClick={onDownload}
              aria-label={downloadLabel}
              className="min-h-10 flex items-center space-x-1 px-3 py-1.5 bg-teal-700/80 hover:bg-teal-600 text-stone-100 rounded-lg text-xs font-medium transition-colors shadow-sm"
            >
              <Download className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{downloadLabel}</span>
            </button>
            {exportActions}
          </div>
        </div>
      </div>

      {/* Main Canvas view */}
      <div
        ref={containerRef}
        onMouseDown={handleCanvasMouseDown}
        className={`relative w-full bg-stone-950 flex items-center justify-center select-none overflow-hidden ${
          zoom > 1 ? 'cursor-grab active:cursor-grabbing' : 'cursor-default'
        }`}
        style={{ height: 'clamp(280px, 58vh, 620px)' }}
      >
        {/* Zoomed & Panned Transformation Container */}
        <div
          className="relative max-w-full max-h-full flex items-center justify-center transition-transform duration-75 ease-out"
          style={{
            transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoom})`,
            transformOrigin: 'center center',
          }}
        >
          {viewMode === 'before' && (
            <img
              src={originalUrl}
              alt={beforeLabel}
              className="max-h-full max-w-full object-contain rounded pointer-events-none select-none"
            />
          )}

          {viewMode === 'after' && (
            <img
              src={restoredUrl}
              alt={afterLabel}
              className="max-h-full max-w-full object-contain rounded pointer-events-none select-none"
            />
          )}

          {(viewMode === 'split' || viewMode === 'face-zoom') && (
            <div className="relative inline-block max-h-full">
              {/* After image (Bottom layer) */}
              <img
                src={restoredUrl}
                alt={afterLabel}
                className="max-h-full max-w-full object-contain block pointer-events-none select-none rounded"
              />

              {/* Before image (Clipped Top layer) */}
              <div
                className="absolute inset-0 overflow-hidden pointer-events-none select-none"
                style={{ width: `${sliderPosition}%` }}
              >
                <img
                  src={originalUrl}
                  alt={beforeLabel}
                  className="max-h-full max-w-none h-full object-contain pointer-events-none select-none rounded"
                />
              </div>

              {/* Slider separator bar (Interactive Divider) */}
              <div
                className="absolute top-0 bottom-0 z-20 cursor-ew-resize"
                style={{ left: `${sliderPosition}%` }}
                onPointerDown={handleSliderPointerDown}
              >
                <div className="absolute top-0 bottom-0 w-[2px] bg-teal-400 shadow-[0_0_8px_rgba(25,170,168,0.85)] -translate-x-1/2" />
                <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-teal-500 text-stone-950 flex items-center justify-center shadow-lg border-2 border-stone-950 hover:scale-110 transition-transform">
                  <Columns className="w-4 h-4" />
                </div>
              </div>

              {/* Overlay labels */}
              <div className="absolute bottom-3 left-3 z-10 pointer-events-none">
                <span className="px-2 py-1 text-[11px] font-semibold bg-stone-950/80 backdrop-blur-sm text-teal-200 border border-stone-700 rounded shadow">
                  {beforeLabel}
                </span>
              </div>
              <div className="absolute bottom-3 right-3 z-10 pointer-events-none">
                <span className="px-2 py-1 text-[11px] font-semibold bg-stone-950/80 backdrop-blur-sm text-emerald-300 border border-stone-700 rounded shadow">
                  {afterLabel}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Floating pan guidance badge when zoomed in */}
        {zoom > 1 && (
          <div className="absolute top-3 left-3 z-20 bg-stone-950/85 backdrop-blur px-3 py-1.5 rounded-lg border border-stone-800 text-xs text-teal-300 flex items-center space-x-2 pointer-events-none shadow-lg">
            <Move className="w-3.5 h-3.5 text-teal-400" />
            <span>Click and drag anywhere to inspect face likeness & details</span>
          </div>
        )}
      </div>
    </div>
  );
};
